import { MLSListing, RegressionOutputSuccess } from '@aloft/toolkit-shared';

import { RegressionScatterPoint } from './types.ts';

const toDollars = (unitAmount: number) => unitAmount / 100;

export const computePredictedSalePrice = (
  listing: MLSListing,
  output: RegressionOutputSuccess,
): number => {
  const { constant, unitRates } = output;

  const gla = listing.LivingArea ?? 0;
  const bedrooms = listing.BedroomsTotal ?? 0;
  const fullBaths = listing.BathroomsFull ?? 0;
  const halfBaths = listing.BathroomsHalf ?? 0;
  const garageCarport = listing.GarageSpaces ?? 0;
  const actualAge = listing.YearBuilt ? new Date().getFullYear() - listing.YearBuilt : 0;
  const siteSize = listing.LotSizeSquareFeet ?? 0;

  const rateFor = (key: keyof typeof unitRates) =>
    unitRates[key].rate ? toDollars(unitRates[key].rate.unitAmount) : 0;

  return (
    toDollars(constant.unitAmount) +
    rateFor('gla') * gla +
    rateFor('bedrooms') * bedrooms +
    rateFor('fullBaths') * fullBaths +
    rateFor('halfBaths') * halfBaths +
    rateFor('garageCarport') * garageCarport +
    rateFor('actualAge') * actualAge +
    rateFor('siteSize') * siteSize
  );
};

export const listingsToScatterData = (
  listings: MLSListing[],
  output: RegressionOutputSuccess,
): RegressionScatterPoint[] =>
  listings.map((listing) => ({
    actualPrice: listing.ClosePrice,
    predictedPrice: Math.round(computePredictedSalePrice(listing, output)),
    gla: listing.LivingArea,
    actualAge: listing.YearBuilt ? new Date().getFullYear() - listing.YearBuilt : undefined,
    bedrooms: listing.BedroomsTotal,
    fullBaths: listing.BathroomsFull,
    halfBaths: listing.BathroomsHalf,
    siteSize: listing.LotSizeSquareFeet,
    garageCarport: listing.GarageSpaces,
  }));

export type BestFitLinePoint = { x: number; y: number };

/**
 * Computes two endpoints of a least-squares linear regression line for the
 * given x/y key pair, returning points keyed by the same field names as the
 * scatter data so they share the same chart axes.
 */
export const computeBestFitLine = (
  data: RegressionScatterPoint[],
  xKey: keyof RegressionScatterPoint,
  yKey: keyof RegressionScatterPoint,
): RegressionScatterPoint[] => {
  const points = data
    .map((d) => ({ x: d[xKey], y: d[yKey] }))
    .filter((p): p is { x: number; y: number } => p.x !== undefined && p.y !== undefined);

  if (points.length < 2) return [];

  const n = points.length;
  const sumX = points.reduce((s, p) => s + p.x, 0);
  const sumY = points.reduce((s, p) => s + p.y, 0);
  const sumXY = points.reduce((s, p) => s + p.x * p.y, 0);
  const sumX2 = points.reduce((s, p) => s + p.x * p.x, 0);

  const denominator = n * sumX2 - sumX * sumX;
  if (denominator === 0) return [];

  const slope = (n * sumXY - sumX * sumY) / denominator;
  const intercept = (sumY - slope * sumX) / n;

  const minX = Math.min(...points.map((p) => p.x));
  const maxX = Math.max(...points.map((p) => p.x));

  return [
    { [xKey]: minX, [yKey]: slope * minX + intercept },
    { [xKey]: maxX, [yKey]: slope * maxX + intercept },
  ] as RegressionScatterPoint[];
};
